import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { api, fmtDate, fmtMoney, formatApiErrorDetail } from "@/lib/apiClient";
import { devLog } from "@/lib/devLog";
import { PageHeader, EmptyState, BcRibbonGroup, BcRibbonBtn } from "@/components/Shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  ArrowsClockwise, Sparkle, Warning, CheckCircle, XCircle, TreeStructure, Calculator,
  Envelope, Microphone, CurrencyDollar, Files, GitDiff, LinkSimple, ArrowRight, ArrowsMerge,
  Fingerprint, ArrowLineDown, ChartLine, Upload,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function confidenceTone(conf) {
  if (conf >= 80) return "text-emerald-700";
  if (conf >= 60) return "text-amber-700";
  return "text-red-700";
}

function docKindLabel(k) {
  if (k === "dc") return "Delivery Challan";
  if (k === "lr") return "Lorry Receipt";
  if (k === "eway") return "E-way Bill";
  return "Bill";
}

function KPI({ label, value, tone = "slate", testid }) {
  const cls = {
    slate: "bg-white border-slate-200 text-slate-900",
    orange: "bg-[#EA580C] text-white border-[#EA580C]",
    dark: "bg-[#0F172A] text-white border-[#0F172A]",
    red: "bg-red-600 text-white border-red-600",
  }[tone];
  return (
    <div className={`border ${cls} rounded-sm p-4`} data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-[0.14em] font-semibold ${tone === "slate" ? "text-slate-500" : "text-white/70"}`}>{label}</div>
      <div className="font-display text-[22px] font-bold font-mono-num mt-1">{value ?? "—"}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

export default function PurchaseGenius() {
  const [stats, setStats] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/purchases-genius/stats"); setStats(data); } catch (e) { devLog.warn(e); }
    setRefreshTick((t) => t + 1);
  }, []);
  useEffect(() => { load(); }, [load]);

  const runBackfill = useCallback(async () => {
    if (!window.confirm("Backfill freight / insurance / packing / other-charges from ALL historical purchase bills into Expenses? This is idempotent — safe to run multiple times.")) return;
    setBackfillBusy(true);
    try {
      const { data } = await api.post("/purchases-genius/backfill-freight-expenses");
      toast.success(`Backfill complete · ${data.created || 0} new expense(s) · ${data.skipped || 0} already synced`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBackfillBusy(false);
    }
  }, [load]);

  return (
    <div data-testid="purchase-genius-page">
      <PageHeader
        title="Purchase Genius"
        module="Procurement · Advanced"
        subtitle="Vendor fingerprinting, duplicate guard, price anomaly detection, 3-way match, landed cost, GSTR-2B reconciliation, multi-doc upload, email/voice capture, multi-currency — everything Tally / Zoho / SAP haven't yet built."
        ribbon={
          <BcRibbonGroup label="Process">
            <BcRibbonBtn icon={ArrowsClockwise} label="Refresh" onClick={load} testid="pg-refresh" />
            <BcRibbonBtn icon={ArrowLineDown} label={backfillBusy ? "Running…" : "Run Backfill"} onClick={runBackfill} testid="pg-backfill" disabled={backfillBusy} />
          </BcRibbonGroup>
        }
      />
      <div className="p-6 space-y-5">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI label="Pending Review" tone={stats.review_pending > 0 ? "orange" : "slate"} value={stats.review_pending} testid="pg-kpi-review" />
            <KPI label="Vendors Learned" tone="dark" value={stats.vendors_learned} testid="pg-kpi-vendors" />
            <KPI label="Bills Awaiting GRN" tone={stats.bills_awaiting_grn > 0 ? "red" : "slate"} value={stats.bills_awaiting_grn} testid="pg-kpi-nogrn" />
            <KPI label="Last GSTR-2B" tone="slate" value={stats.gstr2b_last_month || "Never"} testid="pg-kpi-gstr" />
          </div>
        )}
        <Tabs defaultValue="review" className="w-full" data-testid="pg-tabs">
          <TabsList className="flex flex-wrap h-auto">
            <TabsTrigger value="review" data-testid="tab-review">Review Queue</TabsTrigger>
            <TabsTrigger value="three-way" data-testid="tab-3way">3-Way Match</TabsTrigger>
            <TabsTrigger value="price" data-testid="tab-price">Price Intelligence</TabsTrigger>
            <TabsTrigger value="sku" data-testid="tab-sku">SKU Merger</TabsTrigger>
            <TabsTrigger value="landed" data-testid="tab-landed">Landed Cost</TabsTrigger>
            <TabsTrigger value="fingerprints" data-testid="tab-fp">Vendor Fingerprints</TabsTrigger>
            <TabsTrigger value="gstr2b" data-testid="tab-gstr2b">GSTR-2B Recon</TabsTrigger>
            <TabsTrigger value="multi" data-testid="tab-multi">Multi-Doc</TabsTrigger>
            <TabsTrigger value="voice" data-testid="tab-voice">Voice Entry</TabsTrigger>
            <TabsTrigger value="fx" data-testid="tab-fx">Multi-Currency</TabsTrigger>
            <TabsTrigger value="email" data-testid="tab-email">Email-to-Purchase</TabsTrigger>
          </TabsList>

          <TabsContent value="review" className="mt-5"><ReviewQueuePanel onChange={load} refreshTick={refreshTick} /></TabsContent>
          <TabsContent value="three-way" className="mt-5"><ThreeWayPanel /></TabsContent>
          <TabsContent value="price" className="mt-5"><PriceIntelPanel /></TabsContent>
          <TabsContent value="sku" className="mt-5"><SkuMergerPanel /></TabsContent>
          <TabsContent value="landed" className="mt-5"><LandedCostPanel /></TabsContent>
          <TabsContent value="fingerprints" className="mt-5"><FingerprintsPanel /></TabsContent>
          <TabsContent value="gstr2b" className="mt-5"><Gstr2bPanel /></TabsContent>
          <TabsContent value="multi" className="mt-5"><MultiDocPanel onChange={load} /></TabsContent>
          <TabsContent value="voice" className="mt-5"><VoicePanel onChange={load} /></TabsContent>
          <TabsContent value="fx" className="mt-5"><FxPanel /></TabsContent>
          <TabsContent value="email" className="mt-5"><EmailInstructionsPanel /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ===========================================================================
// Panel: Review Queue
// ===========================================================================
function ReviewQueuePanel({ onChange, refreshTick }) {
  const [rows, setRows] = useState([]);
  const [approving, setApproving] = useState(null);
  const load = useCallback(async () => {
    const { data } = await api.get("/purchases-genius/review-queue?status=pending");
    setRows(data);
  }, []);
  useEffect(() => { load(); }, [load, refreshTick]);

  const reject = async (q) => {
    if (!window.confirm(`Reject this entry from ${q.source}?`)) return;
    try {
      await api.post(`/purchases-genius/review-queue/${q.id}/reject?reason=manual`);
      toast.success("Rejected"); load(); onChange?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-3">
      <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">
        Pending review · {rows.length}
      </div>
      {rows.length === 0 ? (
        <EmptyState title="Nothing to review" description="Bills auto-arrive here from uploads, email inbox, voice capture, and multi-doc bundles." />
      ) : (
        <div className="bc-card border border-slate-200 rounded-sm overflow-hidden">
          <Table className="bc-list">
            <TableHeader>
              <TableRow>
                <TableHead>Source</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Invoice #</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Lines</TableHead>
                <TableHead className="text-right">Confidence</TableHead>
                <TableHead>Warnings</TableHead>
                <TableHead className="w-36"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((q) => {
                const p = q.parsed?.bill || q.parsed || {};
                const conf = q.confidence || 0;
                return (
                  <TableRow key={q.id} data-testid={`review-row-${q.id}`}>
                    <TableCell><span className="text-[10px] uppercase tracking-widest bg-slate-100 border border-slate-200 rounded-sm px-1.5 py-[1px] font-semibold">{q.source}</span></TableCell>
                    <TableCell className="font-semibold">{p.vendor || "—"}</TableCell>
                    <TableCell className="font-mono text-[11.5px]">{p.invoice_number || "—"}</TableCell>
                    <TableCell className="text-[12px] text-slate-600">{p.date ? fmtDate(p.date) : "—"}</TableCell>
                    <TableCell className="text-[12px]">{(p.items || []).length}</TableCell>
                    <TableCell className={`text-right font-mono-num font-semibold ${confidenceTone(conf)}`}>{conf}%</TableCell>
                    <TableCell className="text-[11px]">
                      {q.duplicate_warning?.match && <span className="text-red-700 font-semibold uppercase tracking-widest">Dup risk</span>}
                      {(q.price_warnings || []).length > 0 && <span className="text-amber-700 font-semibold uppercase tracking-widest ml-1">{q.price_warnings.length} price flag(s)</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" className="h-7 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-[11px]" onClick={() => setApproving(q)} data-testid={`review-approve-${q.id}`}><CheckCircle size={11} className="mr-1" /> Approve</Button>
                        <Button size="sm" variant="outline" className="h-7 rounded-sm text-red-600 hover:bg-red-50 border-red-200 text-[11px]" onClick={() => reject(q)} data-testid={`review-reject-${q.id}`}><XCircle size={11} className="mr-1" /> Reject</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {approving && <ApproveDialog q={approving} onClose={() => setApproving(null)} onApproved={() => { setApproving(null); load(); onChange?.(); }} />}
    </div>
  );
}

function ApproveDialog({ q, onClose, onApproved }) {
  const [projects, setProjects] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [dest, setDest] = useState("book");
  const [projectId, setProjectId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [interstate, setInterstate] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data || []));
    api.get("/warehouses").then((r) => setWarehouses(r.data || []));
  }, []);
  const approve = async () => {
    setSaving(true);
    try {
      const body = { destination: dest, is_interstate: interstate };
      if (dest === "site") body.project_id = projectId || null;
      if (dest === "warehouse") body.warehouse_id = warehouseId || null;
      const { data } = await api.post(`/purchases-genius/review-queue/${q.id}/approve`, body);
      toast.success(`Approved → ${data.purchase.vendor} · ₹${data.purchase.total_amount}`);
      onApproved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };
  const p = q.parsed?.bill || q.parsed || {};
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="approve-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Approve → Purchase Bill</DialogTitle>
          <DialogDescription className="sr-only">Review parsed bill data and pick destination before posting.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3 text-[12.5px]">
            <div><span className="label-caps text-slate-500">Vendor</span><div className="font-semibold">{p.vendor || "—"}</div></div>
            <div><span className="label-caps text-slate-500">Invoice #</span><div className="font-mono">{p.invoice_number || "—"}</div></div>
            <div><span className="label-caps text-slate-500">Date</span><div className="font-mono-num">{p.date || "—"}</div></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Destination">
              <Select value={dest} onValueChange={setDest}>
                <SelectTrigger data-testid="approve-dest"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="book">Book only (no stock)</SelectItem>
                  <SelectItem value="site">Site inventory</SelectItem>
                  <SelectItem value="warehouse">Warehouse</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {dest === "site" && (
              <Field label="Project">
                <Select value={projectId} onValueChange={setProjectId}>
                  <SelectTrigger><SelectValue placeholder="Pick" /></SelectTrigger>
                  <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            )}
            {dest === "warehouse" && (
              <Field label="Warehouse">
                <Select value={warehouseId} onValueChange={setWarehouseId}>
                  <SelectTrigger><SelectValue placeholder="Pick" /></SelectTrigger>
                  <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            )}
            <Field label="Interstate?">
              <Select value={interstate ? "yes" : "no"} onValueChange={(v) => setInterstate(v === "yes")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="no">No · CGST + SGST</SelectItem><SelectItem value="yes">Yes · IGST</SelectItem></SelectContent>
              </Select>
            </Field>
          </div>
          <Table className="bc-list text-[12px]">
            <TableHeader><TableRow><TableHead>Item</TableHead><TableHead>HSN</TableHead><TableHead className="text-right">Qty</TableHead><TableHead>UoM</TableHead><TableHead className="text-right">Rate</TableHead><TableHead className="text-right">GST%</TableHead></TableRow></TableHeader>
            <TableBody>{(p.items || []).map((it) => (
              <TableRow key={`${it.sku || it.description}-${it.qty}`}>
                <TableCell>{it.description}</TableCell><TableCell className="font-mono text-[11px]">{it.hsn}</TableCell><TableCell className="text-right font-mono-num">{it.qty}</TableCell><TableCell className="text-[11px] uppercase">{it.unit || "nos"}</TableCell><TableCell className="text-right font-mono-num">{fmtMoney(it.unit_price)}</TableCell><TableCell className="text-right font-mono-num">{it.gst_rate}%</TableCell>
              </TableRow>))}</TableBody>
          </Table>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
            <Button onClick={approve} disabled={saving} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="approve-confirm">{saving ? "Approving…" : "Approve & Post"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===========================================================================
// Panel: 3-Way Match
// ===========================================================================
function ThreeWayPanel() {
  const [purchases, setPurchases] = useState([]);
  const [pid, setPid] = useState("");
  const [result, setResult] = useState(null);
  useEffect(() => { api.get("/purchases").then((r) => setPurchases(r.data || [])); }, []);
  const run = async () => {
    if (!pid) return toast.error("Pick a bill");
    try {
      const { data } = await api.get(`/purchases-genius/three-way-match/${pid}`);
      setResult(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return (
    <div className="space-y-4">
      <div className="border border-slate-200 rounded-sm bg-white p-4">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-3 flex items-center gap-2"><GitDiff size={12} className="text-[#EA580C]" /> Run 3-Way Match</div>
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <Label className="label-caps mb-1 block">Select bill</Label>
            <Select value={pid} onValueChange={setPid}>
              <SelectTrigger data-testid="3way-select"><SelectValue placeholder="Pick a purchase bill" /></SelectTrigger>
              <SelectContent>{purchases.slice(0, 100).map((p) => <SelectItem key={p.id} value={p.id}>{p.vendor} · {p.invoice_number} · {fmtDate(p.date)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={run} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="3way-run">Match →</Button>
        </div>
      </div>
      {result && (
        <>
          <div className="grid grid-cols-4 gap-3">
            <KPI label="Match Status" tone={result.status === "matched" ? "slate" : "red"} value={result.status.replace(/_/g, " ")} />
            <KPI label="PO Found" tone="dark" value={result.po ? result.po.po_number || "yes" : "—"} />
            <KPI label="GRN Found" tone="dark" value={result.grn ? "yes" : "—"} />
            <KPI label="Amount Variance" tone={Math.abs(result.variance.amount_variance_pct) > 2 ? "red" : "slate"} value={`${result.variance.amount_variance_pct}%`} />
          </div>
          {(result.variance.qty_mismatches.length + result.variance.rate_mismatches.length + result.variance.missing_in_grn.length + result.variance.extra_in_bill.length) > 0 && (
            <div className="border border-slate-200 rounded-sm bg-white p-4 space-y-3">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">Discrepancies</div>
              {result.variance.qty_mismatches.map((m) => <div key={`qty-${m.key}`} className="text-[12.5px]"><b>QTY mismatch</b> {m.key}: PO {m.po_qty} → Bill {m.bill_qty}</div>)}
              {result.variance.rate_mismatches.map((m) => <div key={`rate-${m.key}`} className="text-[12.5px]"><b>RATE mismatch</b> {m.key}: PO ₹{m.po_rate} → Bill ₹{m.bill_rate}</div>)}
              {result.variance.missing_in_grn.map((m) => <div key={`missing-${m.key}`} className="text-[12.5px] text-red-700"><b>Missing in Bill</b> {m.key} (PO qty {m.po_qty})</div>)}
              {result.variance.extra_in_bill.map((m) => <div key={`extra-${m.key}`} className="text-[12.5px] text-amber-700"><b>Extra in Bill</b> {m.key} (qty {m.bill_qty})</div>)}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ===========================================================================
// Panel: Price Intelligence
// ===========================================================================
function PriceIntelPanel() {
  const [form, setForm] = useState({ sku: "", name: "", unit_price: 0, days: 90, tolerance_percent: 15 });
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState(null);
  const check = async () => {
    if (!form.sku && !form.name) return toast.error("SKU or name required");
    try {
      const [a, b] = await Promise.all([
        api.post("/purchases-genius/check-price", { ...form, unit_price: Number(form.unit_price), tolerance_percent: Number(form.tolerance_percent), days: Number(form.days) }),
        api.get(`/purchases-genius/price-history?${form.sku ? `sku=${form.sku}` : `name=${encodeURIComponent(form.name)}`}&days=${form.days}`),
      ]);
      setResult(a.data); setHistory(b.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return (
    <div className="space-y-4">
      <div className="border border-slate-200 rounded-sm bg-white p-4">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-3 flex items-center gap-2"><ChartLine size={12} className="text-[#EA580C]" /> Price anomaly check</div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <Field label="SKU"><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="font-mono uppercase" data-testid="price-sku" /></Field>
          <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="price-name" /></Field>
          <Field label="Unit Price (₹)"><Input type="number" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} data-testid="price-value" /></Field>
          <Field label="Tolerance %"><Input type="number" value={form.tolerance_percent} onChange={(e) => setForm({ ...form, tolerance_percent: e.target.value })} /></Field>
          <div className="flex items-end"><Button onClick={check} className="w-full rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="price-check">Check →</Button></div>
        </div>
      </div>
      {result && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Anomaly" tone={result.anomaly ? "red" : "slate"} value={result.anomaly ? "YES" : "NO"} />
          <KPI label="Delta vs Avg" tone={result.anomaly ? "red" : "slate"} value={result.delta_percent ? `${result.delta_percent}%` : "—"} />
          <KPI label="Avg (Last 90d)" tone="dark" value={result.avg_last_days ? `₹${result.avg_last_days}` : "—"} />
          <KPI label="Samples" tone="slate" value={result.samples} />
        </div>
      )}
      {history && history.history?.length > 0 && (
        <div className="border border-slate-200 rounded-sm bg-white overflow-hidden">
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-widest text-slate-600">Purchase history · {history.samples} rows</div>
          <Table className="bc-list">
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Vendor</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Rate</TableHead></TableRow></TableHeader>
            <TableBody>{history.history.slice(-30).map((h, i) => (
              <TableRow key={`h-${h.date}-${h.vendor}-${i}`}>
                <TableCell className="text-[12px]">{fmtDate(h.date)}</TableCell>
                <TableCell>{h.vendor}</TableCell>
                <TableCell className="text-right font-mono-num">{h.qty}</TableCell>
                <TableCell className="text-right font-mono-num font-semibold">{fmtMoney(h.price)}</TableCell>
              </TableRow>))}</TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Panel: SKU Merger
// ===========================================================================
function SkuMergerPanel() {
  const [suggestions, setSuggestions] = useState([]);
  const [threshold, setThreshold] = useState(85);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get(`/purchases-genius/sku-merge-suggestions?threshold=${threshold / 100}`); setSuggestions(data.suggestions || []); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const apply = async (s, canonical) => {
    try {
      await api.post("/purchases-genius/sku-merge-apply", {
        canonical_sku: s.left.sku || s.right.sku || "",
        canonical_name: canonical,
        ids: [s.left, s.right],
      });
      toast.success(`Merged as "${canonical}"`);
      setSuggestions((rows) => rows.filter((r) => !(r.left.name === s.left.name && r.right.name === s.right.name)));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return (
    <div className="space-y-4">
      <div className="border border-slate-200 rounded-sm bg-white p-4">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-3 flex items-center gap-2"><ArrowsMerge size={12} className="text-[#EA580C]" /> Near-duplicate detector</div>
        <div className="flex items-end gap-3">
          <Field label="Similarity ≥ %"><Input type="number" value={threshold} onChange={(e) => setThreshold(Number(e.target.value) || 85)} className="w-28 font-mono-num" /></Field>
          <Button onClick={load} disabled={busy} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="sku-scan">{busy ? "Scanning…" : "Scan →"}</Button>
        </div>
      </div>
      {suggestions.length === 0 ? (
        <EmptyState title="No near-duplicates" description="Run the scan or lower the threshold — inventory looks clean." />
      ) : (
        <div className="space-y-2">
          {suggestions.slice(0, 50).map((s) => (
            <div key={`${s.left.name}--${s.right.name}`} className="border border-slate-200 rounded-sm p-3 bg-white grid grid-cols-[1fr_60px_1fr_auto] gap-2 items-center">
              <div>
                <div className="font-semibold text-[13px]">{s.left.name}</div>
                <div className="text-[11px] text-slate-500">{s.left.source} · SKU {s.left.sku || "—"}</div>
              </div>
              <div className="text-center text-[11px] font-mono-num font-bold text-[#EA580C]">{s.similarity}%</div>
              <div>
                <div className="font-semibold text-[13px]">{s.right.name}</div>
                <div className="text-[11px] text-slate-500">{s.right.source} · SKU {s.right.sku || "—"}</div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" className="rounded-sm h-8 bg-slate-100 hover:bg-slate-200 text-slate-700" onClick={() => apply(s, s.left.name)}>Use L →</Button>
                <Button size="sm" className="rounded-sm h-8 bg-slate-100 hover:bg-slate-200 text-slate-700" onClick={() => apply(s, s.right.name)}>← Use R</Button>
                <Button size="sm" className="rounded-sm h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => apply(s, s.canonical_suggestion)} data-testid="sku-merge-apply">Auto</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Panel: Landed Cost
// ===========================================================================
function LandedCostPanel() {
  const [purchases, setPurchases] = useState([]);
  const [pid, setPid] = useState("");
  const [extras, setExtras] = useState([{ label: "Freight", amount: 0 }]);
  const [basis, setBasis] = useState("value");
  const [result, setResult] = useState(null);
  useEffect(() => { api.get("/purchases").then((r) => setPurchases(r.data || [])); }, []);
  const patch = (i, k, v) => setExtras((e) => e.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const run = async () => {
    if (!pid) return toast.error("Pick a bill");
    if (!extras.some((x) => Number(x.amount) > 0)) return toast.error("Add at least one extra");
    try {
      const { data } = await api.post("/purchases-genius/landed-cost", {
        purchase_id: pid, basis,
        extras: extras.map((x) => ({ ...x, amount: Number(x.amount) })).filter((x) => x.amount > 0),
      });
      setResult(data); toast.success("Landed cost allocated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return (
    <div className="space-y-4">
      <div className="border border-slate-200 rounded-sm bg-white p-4 space-y-3">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 flex items-center gap-2"><Calculator size={12} className="text-[#EA580C]" /> Allocate freight / insurance / customs</div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Label className="label-caps mb-1 block">Bill *</Label>
            <Select value={pid} onValueChange={setPid}>
              <SelectTrigger data-testid="landed-select"><SelectValue placeholder="Pick" /></SelectTrigger>
              <SelectContent>{purchases.slice(0, 100).map((p) => <SelectItem key={p.id} value={p.id}>{p.vendor} · {p.invoice_number} · ₹{p.total_amount}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Field label="Basis">
            <Select value={basis} onValueChange={setBasis}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="value">By line value</SelectItem><SelectItem value="qty">By quantity</SelectItem><SelectItem value="weight">By weight</SelectItem></SelectContent>
            </Select>
          </Field>
        </div>
        <div className="border border-slate-200 rounded-sm">
          <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">Extras</div>
            <button onClick={() => setExtras([...extras, { label: "", amount: 0 }])} className="text-[11px] font-semibold text-[#EA580C]">+ Add</button>
          </div>
          <div className="p-3 space-y-2">
            {extras.map((x, i) => (
              <div key={`extra-${i}`} className="grid grid-cols-[1fr_140px_28px] gap-2">
                <Input placeholder="Freight / Insurance / Customs …" value={x.label} onChange={(e) => patch(i, "label", e.target.value)} className="h-8" />
                <Input type="number" value={x.amount} onChange={(e) => patch(i, "amount", e.target.value)} className="h-8 text-right font-mono-num" data-testid={`landed-amt-${i}`} />
                <button onClick={() => setExtras(extras.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-600 text-xs">×</button>
              </div>
            ))}
          </div>
        </div>
        <Button onClick={run} className="w-full rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="landed-run">Allocate →</Button>
      </div>
      {result && (
        <div className="border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-widest text-slate-600">Landed-cost allocation · Total extras ₹{result.total_extras}</div>
          <Table className="bc-list">
            <TableHeader><TableRow><TableHead>Item</TableHead><TableHead>SKU</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Base rate</TableHead><TableHead className="text-right">Extra allocated</TableHead><TableHead className="text-right">Landed rate</TableHead><TableHead className="text-right">Landed total</TableHead></TableRow></TableHeader>
            <TableBody>{result.allocations.map((a) => (
              <TableRow key={a.sku || a.description}>
                <TableCell>{a.description}</TableCell>
                <TableCell className="font-mono text-[11px]">{a.sku || "—"}</TableCell>
                <TableCell className="text-right font-mono-num">{a.base_qty}</TableCell>
                <TableCell className="text-right font-mono-num">{fmtMoney(a.base_rate)}</TableCell>
                <TableCell className="text-right font-mono-num text-[#EA580C]">{fmtMoney(a.allocated_extra)}</TableCell>
                <TableCell className="text-right font-mono-num font-semibold">{fmtMoney(a.landed_unit_cost)}</TableCell>
                <TableCell className="text-right font-mono-num font-semibold">{fmtMoney(a.landed_total)}</TableCell>
              </TableRow>))}</TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Panel: Vendor Fingerprints
// ===========================================================================
function FingerprintsPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/purchases-genius/vendor-fingerprints").then((r) => setRows(r.data || [])); }, []);
  return (
    <div className="space-y-3">
      <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 flex items-center gap-2"><Fingerprint size={12} className="text-[#EA580C]" /> Learned vendors · {rows.length}</div>
      {rows.length === 0 ? (
        <EmptyState title="No vendors learned yet" description="After you approve a bill for a vendor, the layout is remembered — future bills from the same vendor auto-parse with 0 LLM cost." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {rows.map((v) => (
            <div key={v.id} className="border border-slate-200 rounded-sm bg-white p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold text-slate-900">{v.vendor}</div>
                  <div className="text-[11px] font-mono text-slate-500 mt-0.5">{v.vendor_gstin || "no GSTIN"}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Line items seen</div>
                  <div className="font-mono-num font-bold">{v.line_items_seen}</div>
                </div>
              </div>
              <div className="text-[11px] text-slate-500 mt-2">Typical GST: {v.typical_gst_rate || "—"}% · Last seen: {v.last_seen ? fmtDate(v.last_seen) : "—"}</div>
              <div className="border-t border-slate-100 mt-2 pt-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Common items</div>
                <div className="space-y-1 text-[12px]">
                  {(v.common_items || []).slice(0, 5).map((it, i) => (
                    <div key={`${v.id}-item-${i}`} className="flex justify-between">
                      <span>{it.description}</span>
                      <span className="font-mono-num text-slate-600">₹{it.avg_price} × {it.seen || 1}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Panel: GSTR-2B Reconciliation
// ===========================================================================
function Gstr2bPanel() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [result, setResult] = useState(null);
  const [uploadText, setUploadText] = useState("");
  const reconcile = useCallback(async () => {
    try { const { data } = await api.get(`/purchases-genius/gstr-2b/reconcile?month=${month}`); setResult(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }, [month]);
  const upload = async () => {
    try {
      const body = JSON.parse(uploadText);
      await api.post("/purchases-genius/gstr-2b/upload", { ...body, month });
      toast.success("GSTR-2B uploaded"); reconcile();
    } catch (e) {
      if (e instanceof SyntaxError) toast.error("Invalid JSON");
      else toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { reconcile(); }, [reconcile]);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="border border-slate-200 rounded-sm bg-white p-4 space-y-3">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 flex items-center gap-2"><Upload size={12} className="text-[#EA580C]" /> Upload GSTR-2B JSON</div>
        <Field label="Month">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="font-mono-num" data-testid="2b-month" />
        </Field>
        <Field label={"JSON — {invoices: [{vendor_gstin, invoice_number, date, total}]}"}>
          <Textarea rows={8} value={uploadText} onChange={(e) => setUploadText(e.target.value)} className="font-mono text-[11.5px]" placeholder='{"invoices":[{"vendor_gstin":"27AAA...","invoice_number":"...","date":"2026-02-14","total":50000}]}' data-testid="2b-input" />
        </Field>
        <div className="flex gap-2">
          <Button onClick={upload} className="rounded-sm bg-[#0F172A] hover:bg-black text-white flex-1" data-testid="2b-upload">Upload & Recon</Button>
          <Button variant="outline" onClick={reconcile} className="rounded-sm">Recon only</Button>
        </div>
      </div>
      <div className="space-y-3">
        {result?.uploaded ? (
          <>
            <div className="grid grid-cols-3 gap-3">
              <KPI label="Matched" tone="dark" value={result.totals.matched} />
              <KPI label="Only in 2B" tone="orange" value={result.totals.only_in_2b} />
              <KPI label="Only in Books" tone="red" value={result.totals.only_in_books} />
            </div>
            {result.only_in_2b.length > 0 && (
              <div className="border border-orange-200 bg-orange-50 rounded-sm p-3">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-orange-700 mb-2">Only in 2B (missing ITC in books)</div>
                <ul className="text-[12px] space-y-0.5">{result.only_in_2b.slice(0, 10).map((i, idx) => <li key={`only2b-${i.invoice_number}-${idx}`}><span className="font-mono">{i.invoice_number}</span> · ₹{i.total}</li>)}</ul>
              </div>
            )}
            {result.only_in_books.length > 0 && (
              <div className="border border-red-200 bg-red-50 rounded-sm p-3">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-red-700 mb-2">Only in books (missing in 2B — vendor didn't file)</div>
                <ul className="text-[12px] space-y-0.5">{result.only_in_books.slice(0, 10).map((i, idx) => <li key={`onlybooks-${i.invoice_number}-${idx}`}><span className="font-mono">{i.invoice_number}</span> · {i.vendor} · ₹{i.total}</li>)}</ul>
              </div>
            )}
          </>
        ) : (
          <EmptyState title="No 2B uploaded yet for this month" description="Upload the JSON to see matched / only-in-2B / only-in-books buckets." />
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// Panel: Multi-Doc Upload
// ===========================================================================
function MultiDocPanel({ onChange }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const addFile = (kind, f) => setFiles([...files, { kind, file: f }]);
  const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));
  const upload = async () => {
    if (files.length === 0) return toast.error("Add at least one file");
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach(({ file }) => fd.append("files", file));
      fd.append("kinds", files.map((f) => f.kind).join(","));
      await api.post("/purchases-genius/multi-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${files.length} document(s) → Review Queue`);
      setFiles([]); onChange?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-slate-200 rounded-sm bg-white p-4 space-y-3">
      <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 flex items-center gap-2"><Files size={12} className="text-[#EA580C]" /> Bundle multiple documents</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {["bill", "eway", "lr", "dc"].map((k) => (
          <label key={k} className="border border-dashed border-slate-300 rounded-sm p-3 text-center cursor-pointer hover:border-[#EA580C] hover:bg-slate-50">
            <input type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => e.target.files?.[0] && addFile(k, e.target.files[0])} data-testid={`multi-file-${k}`} />
            <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-700">+ {docKindLabel(k)}</div>
          </label>
        ))}
      </div>
      {files.length > 0 && (
        <div className="border border-slate-200 rounded-sm">
          <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 text-[11px] uppercase font-semibold text-slate-600">Attached · {files.length}</div>
          <div className="p-2 space-y-1">
            {files.map((f, i) => (
              <div key={`${f.file.name}-${i}`} className="flex justify-between items-center text-[12px] bg-slate-50 rounded-sm px-2 py-1">
                <span><b className="uppercase text-slate-500">{f.kind}</b> · {f.file.name}</span>
                <button onClick={() => removeFile(i)} className="text-red-600 text-xs">×</button>
              </div>
            ))}
          </div>
        </div>
      )}
      <Button onClick={upload} disabled={busy || files.length === 0} className="w-full rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="multi-upload">
        {busy ? "Parsing…" : "Parse & Send to Review Queue →"}
      </Button>
    </div>
  );
}

// ===========================================================================
// Panel: Voice Entry
// ===========================================================================
function VoicePanel({ onChange }) {
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [transcript, setTranscript] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => { setAudioBlob(new Blob(chunksRef.current, { type: "audio/webm" })); stream.getTracks().forEach((t) => t.stop()); };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (e) { toast.error("Microphone permission denied"); }
  };
  const stop = () => { mediaRecorderRef.current?.stop(); setRecording(false); };

  const upload = async () => {
    if (!audioBlob) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("audio", audioBlob, "recording.webm");
      const { data } = await api.post("/purchases-genius/voice-parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTranscript(data.transcript);
      toast.success("Transcribed & added to Review Queue");
      onChange?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-slate-200 rounded-sm bg-white p-4 space-y-4">
      <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 flex items-center gap-2"><Microphone size={12} className="text-[#EA580C]" /> Dictate a bill</div>
      <div className="text-[13px] text-slate-600 bg-slate-50 border border-slate-200 rounded-sm p-3">
        <b>Example:</b> "Vendor Bharat Copper, invoice number BCP-2026-041, dated 14 Feb 2026, three items — 50 metres of copper pipe 22mm at 220 rupees, 100 kg cement at 8 rupees, one lot refrigerant R32 at 12500 rupees. All at 18 percent GST."
      </div>
      <div className="flex gap-2">
        {!recording ? (
          <Button onClick={start} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="voice-start"><Microphone size={14} className="mr-1" /> Start Recording</Button>
        ) : (
          <Button onClick={stop} className="rounded-sm bg-red-600 hover:bg-red-700 text-white" data-testid="voice-stop">■ Stop</Button>
        )}
        {audioBlob && (
          <Button onClick={upload} disabled={busy} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="voice-upload">{busy ? "Transcribing…" : "Send to Queue →"}</Button>
        )}
      </div>
      {audioBlob && <audio controls src={URL.createObjectURL(audioBlob)} className="w-full" />}
      {transcript && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-emerald-700 mb-1">Transcript</div>
          <div className="text-[13px] whitespace-pre-wrap">{transcript}</div>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Panel: Multi-currency
// ===========================================================================
function FxPanel() {
  const [form, setForm] = useState({ from_currency: "USD", amount: 1000, date: "" });
  const [result, setResult] = useState(null);
  const run = async () => {
    try {
      const { data } = await api.post("/purchases-genius/convert-currency", { ...form, amount: Number(form.amount), date: form.date || undefined });
      setResult(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return (
    <div className="space-y-4">
      <div className="border border-slate-200 rounded-sm bg-white p-4 space-y-3">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 flex items-center gap-2"><CurrencyDollar size={12} className="text-[#EA580C]" /> Convert to INR at invoice-date rate</div>
        <div className="grid grid-cols-4 gap-3">
          <Field label="Currency">
            <Select value={form.from_currency} onValueChange={(v) => setForm({ ...form, from_currency: v })}>
              <SelectTrigger data-testid="fx-currency"><SelectValue /></SelectTrigger>
              <SelectContent>{["USD", "EUR", "GBP", "AED", "SGD", "JPY", "CNY", "AUD"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={`Amount (${form.from_currency})`}><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="fx-amount" /></Field>
          <Field label="Invoice date"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
          <div className="flex items-end"><Button onClick={run} className="w-full rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="fx-convert">Convert →</Button></div>
        </div>
      </div>
      {result && (
        <div className="grid grid-cols-3 gap-3">
          <KPI label={`Rate ${result.from} → INR`} tone="dark" value={result.rate?.toFixed(4)} />
          <KPI label="Source" tone="slate" value={result.source} />
          <KPI label={`${result.amount} ${result.from} =`} tone="orange" value={`₹${result.converted}`} />
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// Panel: Email instructions
// ===========================================================================
function EmailInstructionsPanel() {
  return (
    <div className="border border-slate-200 rounded-sm bg-white p-6 space-y-4 max-w-3xl">
      <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 flex items-center gap-2"><Envelope size={12} className="text-[#EA580C]" /> Email-to-Purchase — forward bills to Celsius</div>
      <div className="text-[13.5px] text-slate-700 leading-relaxed">
        The webhook <code className="bg-slate-100 px-1 py-0.5 rounded font-mono text-[12px]">POST /api/purchases-genius/email-webhook</code> accepts inbound emails and parses the first PDF/image attachment into the Review Queue.
      </div>
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 text-[12.5px] space-y-2">
        <div><b>To enable in production:</b></div>
        <ol className="list-decimal ml-5 space-y-1">
          <li>Sign up for <b>Mailgun</b>, <b>Postmark</b>, or <b>SendGrid Inbound Parse</b> (free tier is fine).</li>
          <li>Set the inbound webhook URL to <code className="font-mono text-[11.5px]">https://erp.celsius.co.in/api/purchases-genius/email-webhook</code>.</li>
          <li>Add a shared secret (X-Webhook-Secret header) — I'll wire it into the backend on request.</li>
          <li>Route any address like <code className="font-mono">bills@celsius.co.in</code> to that webhook.</li>
        </ol>
        <div className="pt-2 text-slate-500">Any bill emailed to that address will land in <b>Review Queue → Source: email</b> within seconds.</div>
      </div>
      <div className="text-[12px] text-slate-500">
        Test locally: POST a JSON body <code className="font-mono">{`{ "from_email":"vendor@x.com", "attachments":[{"content_type":"application/pdf","base64":"<base64 pdf>"}] }`}</code> to the endpoint.
      </div>
    </div>
  );
}
